import type { WidgetOptions } from '@/types/Widget';

// Feature flag configuration for widgets
// Add new widget code names and their corresponding feature flags here
const WIDGET_FEATURE_FLAGS: Record<string, string[]> = {
  BenchmarkEngagedRepeatVisits: ['basic_benchmarking_widgets'],
  BenchmarkConversionRate: ['basic_benchmarking_widgets'],
  BenchmarkEngagedDwellTime: ['basic_benchmarking_widgets'],
  PremiumMostCommonPath: ['premium_metrics_are_ready_for_production'],
  PremiumCumulativeArrival: ['premium_metrics_are_ready_for_production'],
  PremiumArrivalsByDay: ['premium_metrics_are_ready_for_production'],
  PremiumZoneOverlap: ['premium_metrics_are_ready_for_production'],
  PremiumTotalEngagedDwellByZone: ['premium_metrics_are_ready_for_production'],
};

/**
 * Check if a widget should be available based on feature flags
 * @param widget - The widget to check
 * @param hasFeature - Function to check if user has a specific feature
 * @returns boolean indicating if widget should be available
 */
export function isWidgetAvailable(
  widget: WidgetOptions,
  hasFeature: (feature: string) => boolean | undefined,
): boolean {
  const requiredFeatures = WIDGET_FEATURE_FLAGS[widget.code_name];

  // If no feature flag is required for this widget, it's available
  if (!requiredFeatures) {
    return true;
  }

  // Check if user has all the required features
  return requiredFeatures.every((feature) => hasFeature(feature) ?? false);
}

/**
 * Filter widgets based on availability and feature flags
 * @param widgets - Array of widgets to filter
 * @param hasFeature - Function to check if user has a specific feature
 * @returns Filtered array of available widgets
 */
export function filterAvailableWidgets(
  widgets: WidgetOptions[],
  hasFeature: (feature: string) => boolean | undefined,
): WidgetOptions[] {
  return widgets
    .filter((widget) => widget.available)
    .filter((widget) => isWidgetAvailable(widget, hasFeature));
}
